/**
 * Как поставить приложение на домашний экран iPhone.
 *
 * Зачем экран вообще есть. Приложение живёт в двух видах: Mini App внутри
 * Telegram и обычный сайт, который iOS умеет поставить ярлыком на домашний
 * экран. Второй вид лучше: он открывается с первого экрана телефона, не
 * требует искать чат, не закрывается вместе с Telegram и не отдаёт
 * полэкрана чужому интерфейсу. Но поставить его в одно нажатие нельзя —
 * Apple не даёт сайту установить себя сам, и весь путь человек проходит
 * руками. Значит, путь надо честно расписать.
 *
 * ПОЧЕМУ ПОРЯДОК ИМЕННО ТАКОЙ. Сначала ярлык, потом вход. Раньше это было
 * жёстким требованием: вход шёл по одноразовой ссылке, и открыв её в Safari,
 * человек гасил её там, а ярлык про этот вход не узнавал — iOS держит для
 * установленного веб-приложения отдельное хранилище. Теперь вход идёт по
 * коду, и погасить его «не в том браузере» нельзя: код набирают там, где
 * сидят. Порядок остался прежним по другой причине — войти один раз в том
 * окне, которым будешь пользоваться, всё равно короче, чем входить дважды.
 */

import { useNavigate } from 'react-router-dom'

/** Адрес, который человек набирает в Safari. */
const PUBLIC_URL = 'app.regymo.ru'

interface Step {
  title: string
  detail?: string
}

const STEPS: Step[] = [
  {
    title: `Открой Safari и набери ${PUBLIC_URL}`,
    detail:
      'Именно Safari, а не браузер внутри Telegram: ярлык на домашний экран умеет ставить только он. Входить пока не нужно — приложение откроется и попросит вход, это нормально.',
  },
  {
    title: 'Нажми «Поделиться» — квадрат со стрелкой вверх',
    detail: 'Он внизу экрана, в середине панели Safari.',
  },
  {
    title: 'Выбери «На экран „Домой“» и нажми «Добавить»',
    detail: 'Значок появится на домашнем экране рядом с обычными приложениями.',
  },
  {
    title: 'Открой значок с домашнего экрана',
    detail: 'Приложение откроется на весь экран, без адресной строки Safari.',
  },
  {
    title: 'В Telegram напиши боту /login',
    detail:
      'Он пришлёт короткий код из восьми знаков. Ничего копировать не нужно — код читается с экрана.',
  },
  {
    title: 'Вернись в приложение и набери код',
    detail:
      'Поле на экране «Нужен вход». Регистр и дефис не важны. Дальше вход держится сам: повторять его не придётся.',
  },
]

export default function Install() {
  const navigate = useNavigate()

  return (
    <div className="screen">
      <h1>Приложение на домашнем экране</h1>
      <p className="muted">
        Чтобы открывать тренировку с первого экрана телефона, а не искать чат в Telegram.
      </p>
      {/* Не рекламная строка, а объяснение поведения, которое иначе выглядит
          поломкой. Safari чистит данные сайта, если им не пользовались неделю,
          и человек, заходящий через браузер от случая к случаю, будет
          регулярно получать «нужен вход». На ярлык это правило не
          распространяется. */}
      <p className="faint">
        Ещё одна причина: Safari стирает данные сайта, если не открывать его неделю.
        Вход от этого больше не теряется — сессия лежит ещё и в куке, которую браузер
        так не чистит, — но всё остальное приложение открывает быстрее из ярлыка.
      </p>

      <ol className="install-steps">
        {STEPS.map((step, index) => (
          <li key={step.title}>
            <div className="install-title">
              <span className="install-num tnum">{index + 1}</span>
              {step.title}
            </div>
            {step.detail && <div className="faint install-detail">{step.detail}</div>}
          </li>
        ))}
      </ol>

      <div className="card tight">
        <b>Если что-то пошло не так</b>
        <ul className="tq-list" style={{ marginTop: 8 }}>
          <li>
            Нет пункта «На экран „Домой“» — значит открыт не Safari, а встроенный
            браузер. Набери адрес прямо в Safari, как в первом шаге.
          </li>
          <li>
            Код не подошёл — он живёт минуты и срабатывает один раз. Попроси новый
            командой /login.
          </li>
          <li>
            Ярлык просит вход после обновления телефона — набери новый код, данные
            тренировок от этого не теряются: они на сервере.
          </li>
        </ul>
      </div>

      <div className="faint" style={{ marginTop: 14 }}>
        На Android то же самое делается в Chrome: меню «⋮» → «Установить приложение».
      </div>

      {/* На корень, а не «назад»: экран открывают и по прямой ссылке, где
          истории нет вовсе, и `navigate(-1)` там не делает ничего. */}
      <button className="btn wide" style={{ marginTop: 16 }} onClick={() => navigate('/')}>
        Назад
      </button>
    </div>
  )
}
